import { formatAccountType } from '@/util/account-type-format';
import type { BankLinkWallet, BankUnlinkCheckResponse } from '@/util/bank-link';
import type { BankAccountData } from '@/util/plaid-util';
import { getAccountFundingPlaidId } from './deposit-account-selection';

/** One bank login: every account Plaid shares under it is removed together. */
export type BankConnection = {
  key: string;
  /** Plaid item id. Unlink by this whenever it is known. */
  itemId?: string;
  /**
   * @deprecated ENG-2425: Plaid access token, kept only as a fallback for legacy rows
   * whose item_id is empty. Never used when `itemId` is known.
   */
  accessToken?: string;
  institutionName: string;
  institutionLogo: string | null;
  accounts: BankAccountData[];
  /** False when the accounts carry neither an item id nor a token to unlink by. */
  canUnlink: boolean;
};

/** Where the pre-unlink check for one bank stands. Absent when no check is needed. */
export type BankUnlinkCheckState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; check: BankUnlinkCheckResponse };

const connectionKey = (
  account: BankAccountData,
  itemIdByAccessToken: ReadonlyMap<string, string>,
): string | undefined =>
  account.item_id ||
  (account.access_token && itemIdByAccessToken.get(account.access_token)) ||
  account.access_token ||
  undefined;

export const groupBankConnections = (accounts: readonly BankAccountData[]): BankConnection[] => {
  // An older row of an Item may lack its item id while a newer one has it. Both
  // share a token, so the token tells us which Item the older row belongs to.
  const itemIdByAccessToken = new Map<string, string>();
  accounts.forEach((account) => {
    if (account.item_id && account.access_token) {
      itemIdByAccessToken.set(account.access_token, account.item_id);
    }
  });
  const connections = new Map<string, BankConnection>();
  accounts.forEach((account) => {
    const sharedKey = connectionKey(account, itemIdByAccessToken);
    const key = sharedKey ?? `account:${account.account_id}`;
    const itemId =
      account.item_id ||
      (account.access_token ? itemIdByAccessToken.get(account.access_token) : undefined);
    const existing = connections.get(key);
    if (existing) {
      existing.accounts.push(account);
      existing.itemId = existing.itemId || itemId || undefined;
      existing.accessToken = existing.accessToken || account.access_token || undefined;
      existing.institutionLogo = existing.institutionLogo || account.institution_logo || null;
      return;
    }
    connections.set(key, {
      key,
      itemId: itemId || undefined,
      accessToken: account.access_token || undefined,
      institutionName: account.institution_name || account.name || 'Bank',
      institutionLogo: account.institution_logo || null,
      accounts: [account],
      canUnlink: Boolean(sharedKey),
    });
  });
  return [...connections.values()].sort((a, b) =>
    a.institutionName.localeCompare(b.institutionName),
  );
};

/**
 * Every access token seen on this bank's rows (one Item can hold several logins).
 * Only used locally, to recognise a legacy token-only row of this bank in a
 * refreshed snapshot after an unlink; never sent when the item id is known.
 */
export const bankMatchTokens = (bank: Pick<BankConnection, 'accounts'>): string[] => [
  ...new Set(bank.accounts.flatMap((account) => account.access_token || [])),
];

/**
 * Whether a (refreshed) account belongs to the bank that was just unlinked. Matches
 * the item id or any token the bank was seen with: a legacy row merged into the
 * Item via a shared token has no item_id of its own, and the delete-by-item
 * endpoint only removes rows carrying the id, so item_id alone would miss it.
 */
export const accountBelongsToBank =
  (bank: { itemId?: string; matchTokens: readonly string[] }) =>
  (account: BankAccountData): boolean =>
    (Boolean(bank.itemId) && account.item_id === bank.itemId) ||
    (Boolean(account.access_token) && bank.matchTokens.includes(account.access_token as string));

/**
 * The ids Apex might know this bank's accounts by. A firm-data account normally
 * carries the Plaid Cash id as `plaid_account_id`, but an older row may not, in
 * which case only its own id is left. Sending both costs nothing: an id Apex has
 * never seen matches nothing.
 */
export const bankPlaidIds = (bank: BankConnection): string[] => [
  ...new Set(
    bank.accounts.flatMap((account) => [getAccountFundingPlaidId(account), account.account_id]),
  ),
];

const listNames = (names: readonly string[]): string => {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

export const pendingTransferReason = (bankName: string): string =>
  `A deposit or withdrawal through ${bankName} is still in progress. You can unlink it once that finishes.`;

/** Why this bank cannot be unlinked right now, if it cannot. */
export const unlinkBlockReason = (
  bank: BankConnection,
  checkState: BankUnlinkCheckState | undefined,
): string | undefined => {
  if (!bank.canUnlink) {
    return "We can't unlink this bank right now. Contact support if this keeps happening.";
  }
  if (checkState?.status === 'loading') {
    return 'Checking for transfers in progress…';
  }
  if (checkState?.status === 'error') {
    return "We couldn't check for transfers in progress. Try again in a moment.";
  }
  if (checkState?.status === 'ready' && !checkState.check.can_unlink) {
    return pendingTransferReason(bank.institutionName);
  }
  return undefined;
};

/** The name the investor knows an Apex account by, given its Apex account id. */
export type ApexAccountName = (apexAccountId: string) => string | undefined;

/**
 * Apex account names by Apex account id. Apex itself only knows an account as
 * `accounts/<id>`, so the unlink check cannot name it. The wealth snapshot carries
 * the display name firm-data shows ("Just Invest"); otherwise the wallet's goal
 * is named the way the bank picker names it.
 */
export const apexAccountNames = (
  accounts: readonly BankAccountData[],
  wallets: readonly BankLinkWallet[],
): ApexAccountName => {
  const names = new Map<string, string>();
  wallets.forEach((wallet) => {
    const goal = formatAccountType(wallet.goal);
    if (goal) {
      names.set(wallet.account_id, goal);
    }
  });
  accounts.forEach((account) => {
    const apexAccountId = account.custodian_account_id;
    if (account.custodian_code === 'APEX' && apexAccountId && account.name.trim()) {
      names.set(apexAccountId, account.name.trim());
    }
  });
  return (apexAccountId) => names.get(apexAccountId);
};

const apexAccountLabels = (
  accountIds: readonly string[],
  accountName: ApexAccountName,
): string[] => {
  const named = accountIds.flatMap((accountId) => {
    const name = accountName(accountId);
    return name ? [name] : [];
  });
  const unnamed = accountIds.length - named.length;
  if (unnamed === 0) {
    return named;
  }
  if (named.length === 0) {
    return [unnamed === 1 ? 'one of your Apex accounts' : `${unnamed} of your Apex accounts`];
  }
  return [...named, unnamed === 1 ? 'one other Apex account' : `${unnamed} other Apex accounts`];
};

/** The Apex accounts in plain English, never by their Apex resource name. */
export const apexAccountsText = (
  accountIds: readonly string[],
  accountName: ApexAccountName,
): string => listNames(apexAccountLabels(accountIds, accountName));

/** Tells the user that unlinking also removes the bank from their Apex accounts. */
export const apexRemovalNotice = (
  bank: BankConnection,
  checkState: BankUnlinkCheckState | undefined,
  accountName: ApexAccountName = () => undefined,
): string | undefined => {
  if (checkState?.status !== 'ready') {
    return undefined;
  }
  const accountIds = checkState.check.affected_accounts.map((account) => account.account_id);
  if (accountIds.length === 0) {
    return undefined;
  }
  return `Unlinking ${bank.institutionName} also removes it as a funding bank for ${apexAccountsText(accountIds, accountName)}. You'll need to choose a new bank before you can deposit or withdraw there.`;
};
